/**
 * Renderizações tabulares (padrão contratos/TicketList) dos relatórios
 * Extrato, Carga de trabalho, Executivo e Catálogo — componentes puramente
 * apresentacionais + montadores de CSV co-locados (mesmas colunas da tela).
 */
import type {
  AppointmentsExtractReport,
  CatalogReport,
  ExecutiveReport,
  WorkloadReport,
} from '@erp/shared';
import { FileDownIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { formatDateTime } from '@/lib/utils';
import {
  csvDecimal,
  fmtDecimal,
  minutesToHmm,
  PctChip,
  TD_NUM,
  TH,
  TH_NUM,
  TicketLink,
} from './report-format';

export interface CsvTable {
  headers: string[];
  rows: (string | number | null)[][];
}

// ── 1. Extrato de apontamentos ──────────────────────────────────────────────

export interface ExtractPagination {
  page: number;
  pageCount: number;
  /** linhas do período inteiro (rowsTotal) */
  total: number;
  onPage: (page: number) => void;
}

/**
 * O PDF de UM atendente (PoolTrack, 03/10: "baixar em PDF o relatório de cada
 * atendente"): o botão mora na linha "Total — <nome>", que lista TODOS do
 * filtro em qualquer página (os totais cobrem o período inteiro).
 */
export interface ExtractPdfActions {
  onDownload: (attendant: { userId: number; userName: string }) => void;
  /** quem está sendo baixado agora (o botão dele mostra que está gerando) */
  busyUserId: number | null;
  /** algum download em curso: os outros botões esperam */
  busy: boolean;
}

/**
 * A página de linhas + os totais do período INTEIRO no rodapé da tabela (a
 * API os soma sobre o filtro todo, iguais em toda página) + o paginador.
 */
export function ExtractView({
  data,
  pagination,
  pdf,
}: {
  data: AppointmentsExtractReport;
  pagination: ExtractPagination;
  pdf?: ExtractPdfActions;
}) {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted text-left text-[13px]">
              <th className={TH}>Atendente</th>
              <th className={TH}>Ticket</th>
              <th className={TH}>Título</th>
              <th className={TH}>Mesa</th>
              <th className={TH}>Início</th>
              <th className={TH}>Fim</th>
              <th className={TH_NUM}>Minutos</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              // página além da última (URL editada, ou o período encolheu)
              <tr className="h-10">
                <td colSpan={7} className="px-4 text-center text-[13px] text-muted-foreground">
                  Nenhum apontamento nesta página
                </td>
              </tr>
            ) : null}
            {data.rows.map((r, i) => (
              <tr
                key={i}
                className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
              >
                <td className="max-w-44 truncate px-4 font-medium" title={r.userName}>
                  {r.userName}
                </td>
                <td className="px-4 whitespace-nowrap">
                  <TicketLink number={r.ticketNumber} />
                </td>
                <td className="max-w-72 truncate px-4 text-muted-foreground" title={r.ticketTitle}>
                  {r.ticketTitle}
                </td>
                <td className="max-w-40 truncate px-4 text-muted-foreground" title={r.deskName}>
                  {r.deskName}
                </td>
                <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                  {formatDateTime(r.startedAt)}
                </td>
                <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                  {r.endedAt !== null ? (
                    formatDateTime(r.endedAt)
                  ) : (
                    <span className="italic">em andamento</span>
                  )}
                </td>
                <td className={TD_NUM}>{minutesToHmm(r.minutes)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {data.totalsByUser.map((t) => (
              <tr key={t.userId} className="h-9 border-t border-divider bg-muted/40 text-[13px]">
                <td colSpan={6} className="px-4 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 truncate">
                      Total — <span className="font-medium text-foreground">{t.userName}</span>
                    </span>
                    {pdf ? (
                      <Button
                        size="xs"
                        variant="ghost"
                        // ao lado do nome, e não na ponta da célula: a tabela rola na
                        // horizontal e a ponta fica fora da tela
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => pdf.onDownload({ userId: t.userId, userName: t.userName })}
                        disabled={pdf.busy}
                        aria-label={`Baixar PDF do extrato de ${t.userName}`}
                        data-tour="relatorios-pdf-atendente"
                        title={`Baixar o extrato de ${t.userName} em PDF (o período inteiro, com os filtros aplicados)`}
                      >
                        <FileDownIcon />
                        {pdf.busyUserId === t.userId ? 'Gerando…' : 'PDF'}
                      </Button>
                    ) : null}
                  </div>
                </td>
                <td className={`${TD_NUM} font-medium`}>{minutesToHmm(t.totalMinutes)}</td>
              </tr>
            ))}
            <tr className="h-10 border-t bg-muted text-sm font-semibold">
              <td colSpan={6} className="px-4">
                Total geral
              </td>
              <td className={TD_NUM}>{minutesToHmm(data.totalMinutes)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {/* Rodapé: contagem + paginação (o padrão da tela de clientes) */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
        <span className="text-[13px] text-muted-foreground">
          {pagination.total} apontamento{pagination.total === 1 ? '' : 's'} — página{' '}
          {pagination.page} de {pagination.pageCount}
        </span>
        <Pagination
          page={pagination.page}
          pageCount={pagination.pageCount}
          onPage={pagination.onPage}
        />
      </div>
    </>
  );
}

export function extractCsv(data: AppointmentsExtractReport): CsvTable {
  return {
    headers: ['Atendente', 'Ticket', 'Título', 'Mesa', 'Início', 'Fim', 'Minutos'],
    rows: data.rows.map((r) => [
      r.userName,
      r.ticketNumber,
      r.ticketTitle,
      r.deskName,
      formatDateTime(r.startedAt),
      r.endedAt !== null ? formatDateTime(r.endedAt) : 'em andamento',
      r.minutes,
    ]),
  };
}

// ── 2. Carga de trabalho ────────────────────────────────────────────────────

export function WorkloadView({ data }: { data: WorkloadReport }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted text-left text-[13px]">
            <th className={TH}>Atendente</th>
            <th className={TH_NUM}>Tickets abertos</th>
            <th className={TH_NUM}>Timers ativos</th>
            <th className={TH_NUM}>Minutos (últimos 7 dias)</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr
              key={r.userId ?? 'none'}
              className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
            >
              <td className="max-w-56 truncate px-4">
                {r.userId === null ? (
                  <span className="italic text-muted-foreground">Sem responsável</span>
                ) : (
                  <span className="font-medium">{r.userName}</span>
                )}
              </td>
              <td className={TD_NUM}>{r.openTickets}</td>
              <td className={TD_NUM}>{r.runningTimers}</td>
              <td className={TD_NUM} title={`${r.minutesLast7d} min`}>
                {minutesToHmm(r.minutesLast7d)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function workloadCsv(data: WorkloadReport): CsvTable {
  return {
    headers: ['Atendente', 'Tickets abertos', 'Timers ativos', 'Minutos (últimos 7 dias)'],
    rows: data.rows.map((r) => [
      r.userId === null ? 'Sem responsável' : r.userName,
      r.openTickets,
      r.runningTimers,
      r.minutesLast7d,
    ]),
  };
}

// ── 4. Executivo (por mesa) ─────────────────────────────────────────────────

export function ExecutiveView({ data }: { data: ExecutiveReport }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted text-left text-[13px]">
            <th className={TH}>Mesa</th>
            <th className={TH_NUM}>Criados</th>
            <th className={TH_NUM}>Fechados</th>
            <th className={TH_NUM}>Cancelados</th>
            <th className={TH_NUM}>Média de solução (h)</th>
            <th className={TH}>% SLA atendimento</th>
            <th className={TH}>% SLA solução</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr
              key={r.deskId}
              className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
            >
              <td className="max-w-56 truncate px-4 font-medium" title={r.deskName}>
                {r.deskName}
              </td>
              <td className={TD_NUM}>{r.created}</td>
              <td className={TD_NUM}>{r.closed}</td>
              <td className={TD_NUM}>{r.canceled}</td>
              <td className={TD_NUM}>{fmtDecimal(r.avgSolveHours)}</td>
              <td className="px-4 whitespace-nowrap">
                <PctChip value={r.slaAttendMetPct} />
              </td>
              <td className="px-4 whitespace-nowrap">
                <PctChip value={r.slaSolveMetPct} />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="h-10 border-t bg-muted text-sm font-semibold">
            <td className="px-4">Total</td>
            <td className={TD_NUM}>{data.totals.created}</td>
            <td className={TD_NUM}>{data.totals.closed}</td>
            <td className={TD_NUM}>{data.totals.canceled}</td>
            <td className={TD_NUM}>{fmtDecimal(data.totals.avgSolveHours)}</td>
            <td className="px-4 whitespace-nowrap">
              <PctChip value={data.totals.slaAttendMetPct} />
            </td>
            <td className="px-4 whitespace-nowrap">
              <PctChip value={data.totals.slaSolveMetPct} />
            </td>
          </tr>
        </tfoot>
      </table>
      <CloseReasonsTable data={data} />
    </div>
  );
}

/**
 * Fechados por motivo (spec 054): só aparece quando alguma mesa do recorte tem
 * fechamento com motivo. O "sem motivo" de cada mesa entra ao lado, para a
 * soma bater com a coluna Fechados de cima.
 */
function CloseReasonsTable({ data }: { data: ExecutiveReport }) {
  const rows = data.closeReasons ?? [];
  const desksWithReason = new Set(rows.filter((r) => r.reasonId !== null).map((r) => r.deskId));
  const shown = rows.filter((r) => desksWithReason.has(r.deskId));
  if (shown.length === 0) return null;
  const closedOf = new Map(data.rows.map((r) => [r.deskId, r.closed]));
  return (
    <div className="mt-6">
      <h3 className="px-4 pb-2 text-sm font-semibold">Fechados por motivo</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted text-left text-[13px]">
            <th className={TH}>Mesa</th>
            <th className={TH}>Motivo</th>
            <th className={TH_NUM}>Fechados</th>
            <th className={TH_NUM}>% da mesa</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => {
            const total = closedOf.get(r.deskId) ?? 0;
            return (
              <tr
                key={`${r.deskId}-${r.reasonId ?? 'sem'}`}
                className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
              >
                <td className="max-w-56 truncate px-4 font-medium" title={r.deskName}>
                  {r.deskName}
                </td>
                <td className={r.reasonName === null ? 'px-4 text-muted-foreground' : 'px-4'}>
                  {r.reasonName ?? 'Sem motivo'}
                </td>
                <td className={TD_NUM}>{r.closed}</td>
                <td className={TD_NUM}>{total > 0 ? `${Math.round((r.closed / total) * 100)}%` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function executiveCsv(data: ExecutiveReport): CsvTable {
  const row = (name: string, m: ExecutiveReport['totals']): (string | number | null)[] => [
    name,
    m.created,
    m.closed,
    m.canceled,
    csvDecimal(m.avgSolveHours),
    csvDecimal(m.slaAttendMetPct),
    csvDecimal(m.slaSolveMetPct),
  ];
  return {
    headers: [
      'Mesa',
      'Criados',
      'Fechados',
      'Cancelados',
      'Média de solução (h)',
      '% SLA atendimento',
      '% SLA solução',
    ],
    rows: [...data.rows.map((r) => row(r.deskName, r)), row('Total', data.totals)],
  };
}

// ── 5. Catálogo de serviço (catálogo → área → item) ────────────────────────

interface CatalogGroup {
  catalogId: number;
  catalogName: string;
  tickets: number;
  areas: { areaId: number; areaName: string; tickets: number; items: CatalogReport['rows'] }[];
}

function groupCatalog(rows: CatalogReport['rows']): CatalogGroup[] {
  const groups: CatalogGroup[] = [];
  for (const r of rows) {
    let cat = groups.find((g) => g.catalogId === r.catalogId);
    if (!cat) {
      cat = { catalogId: r.catalogId, catalogName: r.catalogName, tickets: 0, areas: [] };
      groups.push(cat);
    }
    let area = cat.areas.find((a) => a.areaId === r.areaId);
    if (!area) {
      area = { areaId: r.areaId, areaName: r.areaName, tickets: 0, items: [] };
      cat.areas.push(area);
    }
    cat.tickets += r.tickets;
    area.tickets += r.tickets;
    area.items.push(r);
  }
  return groups;
}

export function CatalogView({ data }: { data: CatalogReport }) {
  const groups = groupCatalog(data.rows);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted text-left text-[13px]">
            <th className={TH}>Catálogo / Área / Item</th>
            <th className={TH_NUM}>Tickets</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((cat) => (
            <CatalogGroupRows key={cat.catalogId} group={cat} />
          ))}
          {data.withoutCatalog > 0 ? (
            <tr className="h-10 border-b border-divider last:border-0">
              <td className="px-4 italic text-muted-foreground">Sem catálogo</td>
              <td className={TD_NUM}>{data.withoutCatalog}</td>
            </tr>
          ) : null}
        </tbody>
        <tfoot>
          <tr className="h-10 border-t bg-muted text-sm font-semibold">
            <td className="px-4">Total</td>
            <td className={TD_NUM}>{data.total}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function CatalogGroupRows({ group }: { group: CatalogGroup }) {
  return (
    <>
      <tr className="h-10 border-b border-divider bg-muted/40">
        <td className="px-4 font-semibold">{group.catalogName}</td>
        <td className={`${TD_NUM} font-semibold`}>{group.tickets}</td>
      </tr>
      {group.areas.map((area) => (
        <CatalogAreaRows key={area.areaId} area={area} />
      ))}
    </>
  );
}

function CatalogAreaRows({ area }: { area: CatalogGroup['areas'][number] }) {
  return (
    <>
      <tr className="h-9 border-b border-divider text-[13px]">
        <td className="px-4 pl-8 font-medium text-muted-foreground">{area.areaName}</td>
        <td className={`${TD_NUM} text-muted-foreground`}>{area.tickets}</td>
      </tr>
      {area.items.map((item) => (
        <tr
          key={item.itemId}
          className="h-9 border-b border-divider text-[13px] transition-colors last:border-0 hover:bg-accent/60"
        >
          <td className="max-w-96 truncate px-4 pl-12" title={item.itemName}>
            {item.itemName}
          </td>
          <td className={TD_NUM}>{item.tickets}</td>
        </tr>
      ))}
    </>
  );
}

export function catalogCsv(data: CatalogReport): CsvTable {
  const rows: (string | number | null)[][] = data.rows.map((r) => [
    r.catalogName,
    r.areaName,
    r.itemName,
    r.tickets,
  ]);
  if (data.withoutCatalog > 0) rows.push(['Sem catálogo', null, null, data.withoutCatalog]);
  rows.push(['Total', null, null, data.total]);
  return { headers: ['Catálogo', 'Área', 'Item', 'Tickets'], rows };
}
